import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useShareLink } from '@/hooks/useShareLink';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Link2, Copy, CheckCheck, Trash2, Loader2, ExternalLink, UserPlus, X } from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Two-tier permission selector ────────────────────────────────────────────
function TierSelector({ value, onChange }) {
  const opts = [
    { v: 'comment', label: 'Commenter', desc: 'View & comment' },
    { v: 'collaborate', label: 'Collaborator', desc: 'Comment, assign & complete' },
  ];
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="permission-tier-selector">
      {opts.map(o => (
        <button key={o.v} type="button" onClick={() => onChange(o.v)} data-testid={`tier-${o.v}`}
          className="text-left p-2.5 rounded-xl border transition-colors"
          style={{ borderColor: value === o.v ? '#6994c6' : '#e4e4e0', background: value === o.v ? 'rgba(105,148,198,0.08)' : '#fff' }}>
          <p className="text-xs font-semibold" style={{ color: '#1a1a1a' }}>{o.label}</p>
          <p className="text-[11px]" style={{ color: '#888' }}>{o.desc}</p>
        </button>
      ))}
    </div>
  );
}

// ─── Invite a Milk teammate (account-based access) ───────────────────────────
function InviteTeammates({ cardId, open }) {
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState('');
  const [inviteLevel, setInviteLevel] = useState('collaborate');
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    if (!open || !cardId) return;
    setLoading(true);
    axios.get(`${API_BASE}/action-cards/${cardId}/invites`)
      .then(res => setInvites(res.data.invites || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, cardId]);

  const invite = async () => {
    const e = email.trim();
    if (!e) return;
    setInviting(true);
    try {
      const res = await axios.post(`${API_BASE}/action-cards/${cardId}/invite`, { email: e, access_level: inviteLevel });
      setInvites(prev => [...prev.filter(i => i.user_id !== res.data.invited.user_id), res.data.invited]);
      setEmail('');
      toast.success(`Shared with ${res.data.invited.name || e}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not invite this person');
    } finally {
      setInviting(false);
    }
  };

  const remove = async (userId) => {
    setInvites(prev => prev.filter(i => i.user_id !== userId));
    try { await axios.delete(`${API_BASE}/action-cards/${cardId}/invites/${userId}`); }
    catch { toast.error('Failed to remove'); }
  };

  return (
    <div className="space-y-2.5" data-testid="invite-teammates">
      <div>
        <p className="text-sm font-medium" style={{ color: '#1a1a1a' }}>Invite a teammate</p>
        <p className="text-xs" style={{ color: '#888' }}>Give another Milk user access — it appears in their “Shared with me”.</p>
      </div>
      <div className="flex items-center gap-2">
        <input
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') invite(); }}
          placeholder="teammate@email.com"
          type="email"
          data-testid="invite-email-input"
          className="flex-1 h-9 px-3 text-sm rounded-lg border bg-white outline-none focus:border-primary/50"
          style={{ borderColor: '#e4e4e0' }}
        />
        <Button
          onClick={invite}
          disabled={inviting || !email.trim()}
          data-testid="invite-submit-button"
          className="h-9 gap-1.5 shrink-0"
          style={{ background: '#6994c6', color: 'white', border: 'none' }}
        >
          {inviting ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />}Invite
        </Button>
      </div>
      <TierSelector value={inviteLevel} onChange={setInviteLevel} />
      {loading ? (
        <Skeleton className="h-8 w-full rounded-lg" style={{ background: '#f0f0ee' }} />
      ) : invites.length > 0 && (
        <div className="space-y-1.5" data-testid="invite-list">
          {invites.map((inv, i) => (
            <div key={inv.user_id} data-testid={`invite-row-${i}`}
              className="flex items-center gap-2 py-1.5 px-2.5 rounded-lg" style={{ background: '#f5f5f3' }}>
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
                style={{ background: 'rgba(105,148,198,0.18)', color: '#3d6a9e' }}>
                {(inv.name || inv.email || '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium truncate" style={{ color: '#1a1a1a' }}>{inv.name || inv.email}</p>
                {inv.name && <p className="text-[11px] truncate" style={{ color: '#999' }}>{inv.email}</p>}
              </div>
              <button onClick={() => remove(inv.user_id)} data-testid={`invite-remove-${i}`} aria-label="Remove" className="shrink-0 p-1 rounded hover:bg-black/5" style={{ color: '#c0392b' }}>
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function ShareUrlRow({ shareUrl, copied, onCopy, onRevoke, revoking, shareData }) {
  return (
    <div className="space-y-3">
      <div
        className="flex items-center gap-2 p-3 rounded-xl"
        style={{ background: 'rgba(105,148,198,0.07)', border: '1px solid rgba(105,148,198,0.22)' }}
      >
        <p className="flex-1 min-w-0 text-xs font-mono truncate" style={{ color: '#3d6a9e' }}>
          {shareUrl}
        </p>
        <Button
          size="sm"
          onClick={() => onCopy(shareUrl)}
          className="shrink-0 h-7 text-xs gap-1 active:scale-95"
          style={{ background: copied ? '#3a8a5a' : '#6994c6', color: 'white', border: 'none' }}
        >
          {copied ? <><CheckCheck size={11} />Copied!</> : <><Copy size={11} />Copy</>}
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <a
          href={`/a/${shareData.token}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs flex items-center gap-1 hover:underline"
          style={{ color: '#6994c6' }}
        >
          <ExternalLink size={11} />Preview link
        </a>
        <button
          onClick={onRevoke}
          disabled={revoking}
          className="text-xs flex items-center gap-1 hover:underline"
          style={{ color: '#c0392b' }}
        >
          {revoking
            ? <><Loader2 size={11} className="animate-spin" />Revoking…</>
            : <><Trash2 size={11} />Revoke</>
          }
        </button>
      </div>
      <p className="text-xs" style={{ color: '#aaa' }}>
        Created {new Date(shareData.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        {shareData.last_accessed_at && (
          <span> · Last accessed {new Date(shareData.last_accessed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
        )}
      </p>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function ShareModal({ open, onClose, cardId }) {
  const {
    shareData, accessLevel, loading, generating, revoking, copied,
    generate, revoke, copy, updateAccess,
  } = useShareLink(cardId, open);

  const shareUrl = shareData ? `${window.location.origin}/a/${shareData.token}` : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md border-border" style={{ background: '#ffffff', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: '#1a1a1a' }}>
            <Link2 size={18} style={{ color: '#6994c6' }} />
            Share Action Card
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <InviteTeammates cardId={cardId} open={open} />

          <div className="h-px" style={{ background: '#ececec' }} />

          <p className="text-sm" style={{ color: '#555' }}>
            Or generate a secure public link — share with clients, contractors, or anyone, no account required.
          </p>

          <TierSelector value={accessLevel} onChange={updateAccess} />

          {loading ? (
            <Skeleton className="h-14 w-full rounded-xl" style={{ background: '#f0f0ee' }} />
          ) : shareData ? (
            <ShareUrlRow
              shareUrl={shareUrl}
              copied={copied}
              onCopy={copy}
              onRevoke={revoke}
              revoking={revoking}
              shareData={shareData}
            />
          ) : (
            <Button
              onClick={() => generate()}
              disabled={generating}
              className="w-full h-10 gap-2"
              style={{ background: '#6994c6', color: 'white', border: 'none' }}
            >
              {generating
                ? <><Loader2 size={14} className="animate-spin" />Generating…</>
                : <><Link2 size={14} />Generate Share Link</>
              }
            </Button>
          )}

          <p className="text-xs flex items-start gap-1.5" style={{ color: '#aaa' }}>
            <span>🔒</span>
            <span>Private, not indexed. Access = possession of link. Token uses 62¹² unique combinations.</span>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
